/* ============================================================
   ZUKHRUF — Brand sort dropdown
   ------------------------------------------------------------
   The collection / search "Sort by" control is a native <select>.
   Native <option> lists can't be themed (they inherit the OS font,
   a blue highlight row and square corners), so they never match the
   brand. This progressively enhances each sort <select> into a
   custom listbox that mirrors the filter disclosure panels
   (dark surface, gold hairline, serif/mono type).

   The native <select> is kept in the DOM — visually hidden but still
   submitted with the facet form and used as the no-JS fallback — so
   all existing sort/AJAX plumbing keeps working untouched. Choosing
   an option writes the value back to the <select> and dispatches a
   bubbling `input` event, exactly like the native control would.

   The facet form replaces the whole `.sorting` block on every
   sort/filter change, wiping our UI, so a MutationObserver re-runs
   the enhancement after each re-render.
   ============================================================ */
(function () {
  'use strict';

  var ENHANCED = 'data-zk-sort-enhanced';
  var uid = 0;
  var observer = null;

  /* ---- helpers --------------------------------------------- */

  var CARET_SVG =
    '<svg class="zukhruf-select__caret" aria-hidden="true" focusable="false" viewBox="0 0 10 6">' +
    '<path fill="currentColor" fill-rule="evenodd" clip-rule="evenodd" d="M9.354.646a.5.5 0 00-.708 0L5 4.293 1.354.646a.5.5 0 00-.708.708l4 4a.5.5 0 00.708 0l4-4a.5.5 0 000-.708z"/></svg>';

  var CHECK_SVG =
    '<svg class="zukhruf-select__check" aria-hidden="true" focusable="false" viewBox="0 0 12 12">' +
    '<path fill="currentColor" d="M10.28 2.28 4.5 8.06 1.72 5.28.28 6.72 4.5 10.94l7.22-7.22z"/></svg>';

  function text(node) {
    return (node && node.textContent ? node.textContent : '').trim();
  }

  /* ---- controller ------------------------------------------ */

  function ZukhrufSelect(select) {
    this.select = select;
    this.id = 'zk-sort-' + ++uid;
    this.open = false;
    this.activeIndex = select.selectedIndex < 0 ? 0 : select.selectedIndex;
    this.typeBuffer = '';
    this.typeTimer = null;
    this.build();
    this.bind();
  }

  ZukhrufSelect.prototype.build = function () {
    var select = this.select;
    this.nativeWrap = select.closest('.select') || select.parentElement;
    this.field = select.closest('.facet-filters__field, .mobile-facets__sort');

    // Host
    var host = document.createElement('div');
    host.className = 'zukhruf-select';

    // Trigger
    var button = document.createElement('button');
    button.type = 'button';
    button.id = this.id + '-button';
    button.className = 'zukhruf-select__button';
    button.setAttribute('aria-haspopup', 'listbox');
    button.setAttribute('aria-expanded', 'false');
    button.setAttribute('aria-controls', this.id + '-list');

    var value = document.createElement('span');
    value.className = 'zukhruf-select__value';
    value.textContent = text(select.options[select.selectedIndex]);
    button.appendChild(value);
    button.insertAdjacentHTML('beforeend', CARET_SVG);

    // Panel + listbox
    var panel = document.createElement('div');
    panel.className = 'zukhruf-select__panel';

    var list = document.createElement('ul');
    list.id = this.id + '-list';
    list.className = 'zukhruf-select__list';
    list.setAttribute('role', 'listbox');
    list.tabIndex = -1;

    var self = this;
    this.optionEls = Array.prototype.map.call(select.options, function (opt, i) {
      var li = document.createElement('li');
      li.id = self.id + '-opt-' + i;
      li.className = 'zukhruf-select__option' + (opt.selected ? ' is-selected' : '');
      li.setAttribute('role', 'option');
      li.setAttribute('aria-selected', opt.selected ? 'true' : 'false');
      li.dataset.index = i;
      li.insertAdjacentHTML('beforeend', CHECK_SVG);
      var label = document.createElement('span');
      label.className = 'zukhruf-select__option-text';
      label.textContent = text(opt);
      li.appendChild(label);
      list.appendChild(li);
      return li;
    });

    panel.appendChild(list);
    host.appendChild(button);
    host.appendChild(panel);

    // Accessible name: reuse the existing "Sort by" label.
    var label = this.field ? this.field.querySelector('label') : null;
    if (label) {
      if (!label.id) label.id = this.id + '-label';
      label.htmlFor = button.id; // clicking the label opens our control
      button.setAttribute('aria-labelledby', label.id + ' ' + button.id);
      list.setAttribute('aria-labelledby', label.id);
    } else {
      button.setAttribute('aria-label', 'Sort by');
      list.setAttribute('aria-label', 'Sort by');
    }

    // Hide the native control (kept for submission + no-JS fallback).
    this.nativeWrap.classList.add('zukhruf-select__native');
    select.setAttribute('tabindex', '-1');
    select.setAttribute('aria-hidden', 'true');

    this.nativeWrap.insertAdjacentElement('afterend', host);

    this.host = host;
    this.button = button;
    this.valueEl = value;
    this.panel = panel;
    this.list = list;
  };

  ZukhrufSelect.prototype.bind = function () {
    var self = this;

    this.button.addEventListener('click', function () {
      self.open ? self.close(true) : self.openPanel();
    });

    this.button.addEventListener('keydown', function (e) {
      switch (e.key) {
        case 'ArrowDown':
        case 'Down':
        case 'ArrowUp':
        case 'Up':
        case 'Enter':
        case ' ':
        case 'Spacebar':
          e.preventDefault();
          self.openPanel();
          break;
        case 'Escape':
          self.close(false);
          break;
      }
    });

    this.list.addEventListener('keydown', function (e) {
      switch (e.key) {
        case 'ArrowDown':
        case 'Down':
          e.preventDefault();
          self.setActive(self.activeIndex + 1);
          break;
        case 'ArrowUp':
        case 'Up':
          e.preventDefault();
          self.setActive(self.activeIndex - 1);
          break;
        case 'Home':
          e.preventDefault();
          self.setActive(0);
          break;
        case 'End':
          e.preventDefault();
          self.setActive(self.optionEls.length - 1);
          break;
        case 'Enter':
        case ' ':
        case 'Spacebar':
          e.preventDefault();
          self.choose(self.activeIndex);
          break;
        case 'Escape':
          e.preventDefault();
          self.close(true);
          break;
        case 'Tab':
          self.close(false);
          break;
        default:
          if (e.key && e.key.length === 1) self.typeahead(e.key);
      }
    });

    this.list.addEventListener('click', function (e) {
      var li = e.target.closest('.zukhruf-select__option');
      if (li) self.choose(Number(li.dataset.index));
    });

    this.list.addEventListener('mousemove', function (e) {
      var li = e.target.closest('.zukhruf-select__option');
      if (li) self.setActive(Number(li.dataset.index), true);
    });

    this._onDocPointer = function (e) {
      if (!self.host.contains(e.target)) self.close(false);
    };
  };

  ZukhrufSelect.prototype.openPanel = function () {
    if (this.open) return;
    this.open = true;
    this.panel.classList.add('is-open');
    this.button.setAttribute('aria-expanded', 'true');
    this.setActive(this.select.selectedIndex < 0 ? 0 : this.select.selectedIndex);
    this.list.focus();
    document.addEventListener('pointerdown', this._onDocPointer, true);
  };

  ZukhrufSelect.prototype.close = function (focusButton) {
    if (!this.open) return;
    this.open = false;
    this.panel.classList.remove('is-open');
    this.button.setAttribute('aria-expanded', 'false');
    this.list.removeAttribute('aria-activedescendant');
    document.removeEventListener('pointerdown', this._onDocPointer, true);
    if (focusButton) this.button.focus();
  };

  ZukhrufSelect.prototype.setActive = function (i, fromPointer) {
    i = Math.max(0, Math.min(this.optionEls.length - 1, i));
    if (this.activeIndex === i && fromPointer) return;
    this.optionEls.forEach(function (li, idx) {
      li.classList.toggle('is-active', idx === i);
    });
    this.activeIndex = i;
    this.list.setAttribute('aria-activedescendant', this.optionEls[i].id);
    if (!fromPointer) this.optionEls[i].scrollIntoView({ block: 'nearest' });
  };

  ZukhrufSelect.prototype.choose = function (i) {
    var opt = this.select.options[i];
    if (!opt) return;
    var changed = this.select.selectedIndex !== i;

    this.select.selectedIndex = i;
    this.valueEl.textContent = text(opt);
    this.optionEls.forEach(function (li, idx) {
      var sel = idx === i;
      li.classList.toggle('is-selected', sel);
      li.setAttribute('aria-selected', sel ? 'true' : 'false');
    });
    this.close(true);

    if (changed) {
      // Mirror a native user change so the facet form re-renders.
      this.select.dispatchEvent(new Event('input', { bubbles: true }));
      this.select.dispatchEvent(new Event('change', { bubbles: true }));
    }
  };

  ZukhrufSelect.prototype.typeahead = function (char) {
    var self = this;
    clearTimeout(this.typeTimer);
    this.typeBuffer += char.toLowerCase();
    this.typeTimer = setTimeout(function () {
      self.typeBuffer = '';
    }, 500);

    for (var n = 0; n < this.optionEls.length; n++) {
      if (text(this.select.options[n]).toLowerCase().indexOf(this.typeBuffer) === 0) {
        this.setActive(n);
        return;
      }
    }
  };

  /* ---- bootstrap ------------------------------------------- */

  function enhanceAll() {
    var selects = document.querySelectorAll(
      'select[name="sort_by"]:not([' + ENHANCED + '])'
    );
    if (!selects.length) return;
    pauseObserver();
    Array.prototype.forEach.call(selects, function (select) {
      select.setAttribute(ENHANCED, 'true');
      try {
        new ZukhrufSelect(select);
      } catch (err) {
        // If anything goes wrong, leave the readable native control in place.
        select.removeAttribute(ENHANCED);
        if (window.console) console.warn('[zukhruf-select]', err);
      }
    });
    resumeObserver();
  }

  function pauseObserver() {
    if (observer) observer.disconnect();
  }

  function resumeObserver() {
    if (observer) {
      observer.observe(document.body, { childList: true, subtree: true });
    }
  }

  var scheduled = false;
  function scheduleEnhance() {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(function () {
      scheduled = false;
      enhanceAll();
    });
  }

  function init() {
    observer = new MutationObserver(scheduleEnhance);
    observer.observe(document.body, { childList: true, subtree: true });
    enhanceAll(); // initial pass; re-runs after every facet re-render
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
